import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { AmorphicService } from '../../../../services/amorphic/amorphic.service';
import { GeoChangeService } from '../../../../services/geoChange/geoChange.service';
import { Subscription } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ValidatorService } from '../../../../../../common/angular-shared/services/validators-reactive.service';
import { FormatterService } from '../../../../services/formatter/formatter.service';
import { AccountCenterControllerInterface } from '../../../../interfaces/account-center/AccountCenterControllerInterface';
import { AccountCenterService } from '../../../../services/account-center/account-center.service';
import { BasicTextInputComponent } from '../../../form/base-components/base-text-input/basic-text-input.component';

@Component({
  moduleId: module.id,
  selector: 'license-correction-form',
  templateUrl: './license-form.component.html',
})
export class LicenseFormComponent implements OnInit {
  @Input() personType: string;
  @Output() toggleLoadingSpinner: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() submitCorrection: EventEmitter<void> = new EventEmitter<void>();
  @Output() zipChanged: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();
  @ViewChild('idNumberValue') idNumberInput: BasicTextInputComponent;

  personString: string;
  licenseForm: FormGroup;
  changesWereMade: boolean = false;
  zipChangeSubscription: Subscription;
  oldZip: string;
  controller;
  accountCenterController: AccountCenterControllerInterface;
  person;
  headerText: string = 'What is the drivers license information?';
  licenseNumberFromDb: string;

  constructor(
    private formBuilder: FormBuilder,
    private amorphicService: AmorphicService,
    private geoChangeService: GeoChangeService,
    private formatterService: FormatterService
  ) {
    this.controller = amorphicService.controller;
    this.accountCenterController = this.controller.accountCenterController;

    this.licenseForm = this.formBuilder.group({
      licenseNumber: ['', Validators.required],
      licenseState: ['', Validators.required],
      licenseExpiration: ['', [Validators.required, ValidatorService.futureDateValidator]],
    });
  }

  async ngOnInit() {
    this.setPerson();
    await this.initializeLicenseForm();

    this.licenseForm.valueChanges.subscribe(() => {
      this.checkForChanges();
    });

    const licenseNumberFormControl = this.licenseForm.get('licenseNumber');
    const obfuscationPattern = licenseNumberFormControl.value ? '•'.repeat(licenseNumberFormControl.value.length) : '';
    this.idNumberInput.val = licenseNumberFormControl.valid
      ? obfuscationPattern
      : this.licenseForm.get('licenseNumber').value;
  }

  private async initializeLicenseForm(): Promise<void> {
    const date = this.formatterService.formatUTCDate(this.person.idExpires as Date);
    this.licenseNumberFromDb = await this.controller.getPiiValueByKey(this.person.idNumber, 0, false, 'idNumber');

    this.licenseForm.patchValue({
      licenseNumber: this.licenseNumberFromDb,
      licenseState: this.person.idState,
      licenseExpiration: date,
    });
  }

  private setPerson(): void {
    if (this.personType === 'owner') {
      this.personString = 'owner';
      this.person = this.controller.customer.application.primaryPolicy.owner;
      this.headerText = `What is ${this.person.firstName}, the owner's, drivers license information?`;
    } else {
      this.personString = 'insured.person';
      this.person = this.controller.customer.application.primaryPolicy.insured.person;
      this.headerText = `What is ${this.person.firstName}, the insured's, drivers license information?`;
    }
  }

  goToOR1(): void {
    this.toggleLoadingSpinner.emit(true);
    this.accountCenterController.checkRequirement(
      this.controller.customer.application.primaryPolicy,
      'Operations Review',
      'operations review'
    );
  }

  async validateAndContinue(): Promise<void> {
    if (!this.changesWereMade || this.licenseForm.valid === false) {
      return;
    }

    this.toggleLoadingSpinner.emit(true);

    if (
      this.controller.customer.application.primaryPolicy.isCoverpathPolicy() &&
      this.licenseForm.get('licenseNumber').value !== this.licenseNumberFromDb
    ) {
      const piiKey = await this.controller.setPiiByProperty(
        `${this.personString}.idNumber`,
        this.licenseForm.get('licenseNumber').value
      );
      this.person.idNumber = piiKey;
    } else {
      this.person.idNumber = this.licenseForm.get('licenseNumber').value;
    }
    this.person.idState = this.licenseForm.get('licenseState').value;
    this.person.idExpires = this.licenseForm.get('licenseExpiration').value;

    this.controller.publicSave();

    this.submitCorrection.emit();
  }

  showLicenseNumber() {
    this.idNumberInput.val = this.licenseForm.get('licenseNumber').value;
  }

  obfuscateLicenseNumber() {
    const licenseNumberFormControl = this.licenseForm.get('licenseNumber');
    licenseNumberFormControl.patchValue(this.idNumberInput.val);
    licenseNumberFormControl.markAsTouched();

    if (licenseNumberFormControl.valid) {
      this.idNumberInput.val = licenseNumberFormControl.value ? '•'.repeat(licenseNumberFormControl.value.length) : '';
    }
  }

  private checkForChanges(): void {
    const date = this.formatterService.formatUTCDate(this.person.idExpires as Date);

    this.changesWereMade =
      this.licenseForm.get('licenseNumber').value !== this.licenseNumberFromDb ||
      this.licenseForm.get('licenseState').value !== this.person.idState ||
      this.licenseForm.get('licenseExpiration').value !== date;
  }
}
